import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Select } from '../components/ui/Select';
import { Badge } from '../components/ui/Badge';
import { Alert } from '../components/ui/Alert';
import { toast } from 'sonner';
import { apiFetch, parseJsonResponse, isNetworkErrorResponse } from '../utils/api';

interface Tariff {
  id: number;
  establishment_id: number;
  cod_tarifa_hioffice: number;
  tarifa_name: string;
  is_default: number;
  is_active: number;
  display_order: number;
  last_sync: string | null;
  precio_hazlo_combo_regular: number | null;
  precio_hazlo_combo_gigante: number | null;
  created_at: string;
  updated_at: string;
  establishment_name?: string;
}

interface ExportedTariff {
  cod_establecimiento: string;
  establecimiento: string;
  cod_tarifa: string;
  tarifa_venta: string;
  precio_extras: string | null;
  precio_extras_2: string | null;
  precio_hazlo_combo_regular: string | null;
  precio_hazlo_combo_gigante: string | null;
  precio_refresco_regular: string | null;
  kcal_refresco_regular: string | null;
  precio_refresco_large: string | null;
  kcal_refresco_large: string | null;
  precio_refresco_bottle: string | null;
  kcal_refresco_bottle: string | null;
  precio_chips: string | null;
  kcal_chips: string | null;
  precio_cookie: string | null;
  kcal_cookie: string | null;
  precio_brownie: string | null;
  kcal_brownie: string | null;
}

interface Establishment {
  id: number;
  name_establishment: string;
  code_establishment_hioffice: number;
  is_active: number;
}

/**
 * Decodifica Base64 a UTF-8 correctamente
 */
function base64ToUtf8(base64: string): string {
  try {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    const decoder = new TextDecoder('utf-8');
    return decoder.decode(bytes);
  } catch (error) {
    console.error('Error al decodificar Base64 a UTF-8:', error);
    return atob(base64);
  }
}

/**
 * Parsea el CSV exportado y extrae las tarifas
 * Formato: Cód. Establecimiento;Establecimiento;Cód. Tarifa;Tarifa Venta;Precio Extras;Precio Extras 2;...
 */
function parseExportedTariffs(csvData: string): ExportedTariff[] {
  const lines = csvData.split('\n').filter(line => line.trim());
  if (lines.length === 0) return [];
  
  const dataLines = lines.slice(1);
  const tariffs: ExportedTariff[] = [];
  
  // Helper para parsear decimales (pueden tener punto o coma)
  const parseDecimal = (value: string | undefined): string | null => {
    if (!value || value.trim() === '') return null;
    return value.trim().replace(',', '.');
  };
  
  dataLines.forEach((line) => {
    const columns = line.split(';');
    if (columns.length >= 4) {
      const codEstablecimiento = columns[0]?.trim() || '';
      const establecimiento = columns[1]?.trim() || '';
      const codTarifa = columns[2]?.trim() || '';
      const tarifaVenta = columns[3]?.trim() || '';
      
      if (codTarifa && tarifaVenta) {
        tariffs.push({
          cod_establecimiento: codEstablecimiento,
          establecimiento: establecimiento,
          cod_tarifa: codTarifa,
          tarifa_venta: tarifaVenta,
          precio_extras: parseDecimal(columns[4]),
          precio_extras_2: parseDecimal(columns[5]),
          precio_hazlo_combo_regular: parseDecimal(columns[6]),
          precio_hazlo_combo_gigante: parseDecimal(columns[7]),
          precio_refresco_regular: parseDecimal(columns[8]),
          kcal_refresco_regular: parseDecimal(columns[9]),
          precio_refresco_large: parseDecimal(columns[10]),
          kcal_refresco_large: parseDecimal(columns[11]),
          precio_refresco_bottle: parseDecimal(columns[12]),
          kcal_refresco_bottle: parseDecimal(columns[13]),
          precio_chips: parseDecimal(columns[14]),
          kcal_chips: parseDecimal(columns[15]),
          precio_cookie: parseDecimal(columns[16]),
          kcal_cookie: parseDecimal(columns[17]),
          precio_brownie: parseDecimal(columns[18]),
          kcal_brownie: parseDecimal(columns[19])
        });
      }
    }
  });
  
  return tariffs;
}

export const Tariffs: React.FC = () => {
  const { token } = useAuth();
  const [tariffs, setTariffs] = useState<Tariff[]>([]);
  const [establishments, setEstablishments] = useState<Establishment[]>([]);
  const [selectedEstablishmentId, setSelectedEstablishmentId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [loadingEstablishments, setLoadingEstablishments] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [exportedTariffs, setExportedTariffs] = useState<ExportedTariff[]>([]);
  const [selectedTariff, setSelectedTariff] = useState<string>('');

  // Cargar establecimientos
  useEffect(() => {
    const loadEstablishments = async () => {
      if (!token) {
        setLoadingEstablishments(false);
        return;
      }

      setLoadingEstablishments(true);
      try {
        const params = new URLSearchParams({
          page: '1',
          limit: '1000' // Obtener todos los establecimientos
        });

        const route = `establishments/list&${params}`;
        const res = await apiFetch(route, {}, token);
        
        if (isNetworkErrorResponse(res)) {
          setError('Error de conexión al cargar establecimientos');
          return;
        }

        const json = await parseJsonResponse(res);

        if (!res.ok || json.error) {
          const errorMessage = typeof json.error === 'string' ? json.error : json.message || json.data?.error || 'Error al cargar establecimientos';
          throw new Error(errorMessage);
        }

        setEstablishments(json.data?.establishments || []);
      } catch (err: any) {
        console.error('Error cargando establecimientos:', err);
        setError(err.message || 'Error al cargar establecimientos');
      } finally {
        setLoadingEstablishments(false);
      }
    };

    loadEstablishments();
  }, [token]);

  // Cargar tarifas cuando se selecciona un establecimiento
  useEffect(() => {
    if (selectedEstablishmentId && token) {
      loadTariffs();
    } else {
      setTariffs([]);
    }
  }, [selectedEstablishmentId, token]);

  const loadTariffs = async () => {
    if (!token || !selectedEstablishmentId) return;
    
    setLoading(true);
    try {
      const API_BASE_URL = process.env.NODE_ENV === 'production' ? '/api' : 'http://hiomenu.local/api';
      const url = `${API_BASE_URL}.php?route=tariffs/list&establishment_id=${selectedEstablishmentId}`;
      
      const res = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (isNetworkErrorResponse(res)) {
        toast.error('Error de conexión al cargar tarifas');
        return;
      }
      
      const json = await parseJsonResponse(res);
      if (json.data?.tariffs) {
        setTariffs(json.data.tariffs);
      }
    } catch (error) {
      console.error('Error cargando tarifas:', error);
      toast.error('Error al cargar tarifas');
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    if (!token || !selectedEstablishmentId) {
      toast.error('Debes seleccionar un establecimiento');
      return;
    }
    
    setSyncing(true);
    try {
      console.log('🔄 Iniciando sincronización de tarifas...', {
        establishmentId: selectedEstablishmentId
      });
      
      const res = await apiFetch(
        'tariffs/sync',
        {
          method: 'POST',
          body: JSON.stringify({
            establishment_id: parseInt(selectedEstablishmentId, 10)
          })
        },
        token
      );
      
      console.log('📡 Respuesta HTTP recibida:', {
        status: res.status,
        ok: res.ok,
        statusText: res.statusText
      });
      
      if (isNetworkErrorResponse(res)) {
        console.error('❌ Error de red al sincronizar');
        toast.error('Error de conexión al sincronizar');
        return;
      }
      
      const json = await parseJsonResponse(res);
      
      // Log completo de la respuesta
      console.log('📦 Respuesta completa de sincronización:', {
        status: res.status,
        ok: res.ok,
        json: json,
        data: json.data,
        success: json.success,
        error: json.error
      });
      
      if (json.error || !json.success) {
        let errorMessage = 'Error al sincronizar tarifas';
        if (json.error) {
          errorMessage = typeof json.error === 'string' ? json.error : String(json.error);
        } else if (json.message) {
          errorMessage = typeof json.message === 'string' ? json.message : String(json.message);
        }
        
        console.error('❌ Error en sincronización:', {
          error: json.error,
          message: json.message,
          data: json.data,
          errorMessage
        });
        
        toast.error(errorMessage);
      } else {
        const data = json.data || {};
        const loginSuccess = data.loginSuccess ?? false;
        const exportSuccess = data.exportSuccess ?? false;
        
        console.log('✅ Respuesta exitosa:', {
          loginSuccess,
          exportSuccess,
          hasExportData: !!data.export_data,
          exportError: data.exportError
        });
        
        // Mostrar XML del login si está disponible
        if (data.debug_xml_login) {
          console.log('📄 XML recibido de Hioffice al INICIAR SESIÓN:');
          console.log(data.debug_xml_login);
          try {
            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(data.debug_xml_login, 'text/xml');
            console.log('📄 XML parseado del login:', xmlDoc);
            console.log('📄 Estructura XML del login:', xmlDoc.documentElement);
          } catch (parseError) {
            console.warn('⚠️ No se pudo parsear el XML del login:', parseError);
          }
        }
        
        // Mostrar respuesta de exportación si está disponible
        if (data.debug_export_response) {
          console.log('📦 Respuesta recibida de Hioffice al EXPORTAR:');
          console.log(data.debug_export_response);
          try {
            const jsonParsed = JSON.parse(data.debug_export_response);
            console.log('📦 JSON parseado de exportación:', jsonParsed);
            
            // Si es un array, mostrar el primer elemento
            const responseData = Array.isArray(jsonParsed) ? jsonParsed[0] : jsonParsed;
            console.log('📦 Datos de exportación procesados:', responseData);
            
            // Si hay exportedDocs, mostrar información detallada
            if (responseData?.exportedDocs && Array.isArray(responseData.exportedDocs)) {
              console.log(`📦 Total de documentos exportados: ${responseData.exportedDocs.length}`);
              responseData.exportedDocs.forEach((doc: any, index: number) => {
                console.log(`📦 Documento ${index + 1}:`, {
                  name: doc.name,
                  type: doc.type,
                  dataLength: doc.data ? doc.data.length : 0,
                  dataPreview: doc.data ? doc.data.substring(0, 200) + '...' : 'sin datos'
                });
              });
            }
          } catch (parseError) {
            console.warn('⚠️ La respuesta de exportación no es JSON válido:', parseError);
            console.log('📦 Respuesta cruda de exportación:', data.debug_export_response);
          }
        }
        
        // Verificar primero si la exportación fue exitosa
        if (exportSuccess === false) {
          // La exportación falló pero el login fue exitoso
          const errorMsg = data.exportError || 'Error al exportar desde Hioffice';
          console.error('❌ Error en exportación:', {
            loginSuccess,
            exportSuccess,
            exportError: data.exportError,
            errorMsg
          });
          toast.error(`Error en exportación: ${errorMsg}`);
          // Limpiar tarifas exportadas previas
          setExportedTariffs([]);
          return;
        }
        
        // Verificar si hay datos de exportación
        if (data.export_data) {
          console.log('📊 Datos de exportación encontrados:', {
            hasExportedDocs: !!data.export_data.exportedDocs,
            exportedDocsCount: data.export_data.exportedDocs?.length || 0
          });
          
          const exportData = data.export_data;
          
          // Buscar el documento CSV en exportedDocs
          if (exportData.exportedDocs && Array.isArray(exportData.exportedDocs)) {
            console.log(`📦 Total de documentos en export_data: ${exportData.exportedDocs.length}`);
            
            const csvDoc = exportData.exportedDocs.find((doc: any) => doc.type === 1); // type 1 = CSV
            
            if (csvDoc) {
              console.log('📄 Documento CSV encontrado:', {
                name: csvDoc.name,
                type: csvDoc.type,
                dataLength: csvDoc.data ? csvDoc.data.length : 0
              });
              
              if (csvDoc.data) {
                const csvData = base64ToUtf8(csvDoc.data);
                console.log('📄 CSV decodificado (primeros 500 caracteres):', csvData.substring(0, 500));
                
                const parsed = parseExportedTariffs(csvData);
                console.log(`✅ Tarifas parseadas: ${parsed.length}`, parsed);
                
                setExportedTariffs(parsed);
                
                if (parsed.length > 0) {
                  toast.success(`Se encontraron ${parsed.length} tarifa(s) desde Hioffice`);
                } else {
                  toast.info('Sincronización exitosa, pero no se encontraron tarifas en el CSV');
                }
              } else {
                console.warn('⚠️ Documento CSV encontrado pero sin datos');
                toast.info('Sincronización exitosa, pero no se encontraron datos CSV en los documentos exportados');
              }
            } else {
              console.warn('⚠️ No se encontró documento CSV (type = 1) en exportedDocs');
              console.log('📦 Documentos disponibles:', exportData.exportedDocs.map((doc: any) => ({
                name: doc.name,
                type: doc.type
              })));
              toast.info('Sincronización exitosa, pero no se encontraron datos CSV en los documentos exportados');
            }
          } else {
            console.warn('⚠️ export_data.exportedDocs no es un array o no existe');
            toast.info('Sincronización exitosa, pero no se encontraron documentos exportados');
          }
        } else {
          console.warn('⚠️ No hay export_data en la respuesta');
          toast.success('Sincronización exitosa');
        }
        
        // Recargar tarifas guardadas
        loadTariffs();
      }
    } catch (error: any) {
      console.error('❌ Error en sincronización:', error);
      toast.error('Error al sincronizar tarifas');
    } finally {
      setSyncing(false);
    }
  };

  // Función para refrescar la tarifa predeterminada
  const handleRefreshDefaultTariff = async (defaultTariff: Tariff) => {
    if (!token || !selectedEstablishmentId) {
      toast.error('Debes seleccionar un establecimiento');
      return;
    }
    
    setSyncing(true);
    try {
      // Ejecutar sincronización
      const res = await apiFetch(
        'tariffs/sync',
        {
          method: 'POST',
          body: JSON.stringify({
            establishment_id: parseInt(selectedEstablishmentId, 10)
          })
        },
        token
      );
      
      if (isNetworkErrorResponse(res)) {
        toast.error('Error de conexión al sincronizar');
        return;
      }
      
      const json = await parseJsonResponse(res);
      
      if (json.error || !json.success) {
        const errorMessage = typeof json.error === 'string' ? json.error : json.message || 'Error al sincronizar tarifas';
        toast.error(errorMessage);
        return;
      }
      
      const data = json.data || {};
      const exportSuccess = data.exportSuccess ?? false;
      
      if (!exportSuccess) {
        const errorMsg = data.exportError || 'Error al exportar desde Hioffice';
        toast.error(`Error en exportación: ${errorMsg}`);
        return;
      }
      
      // Buscar la tarifa predeterminada en los datos exportados
      if (data.export_data?.exportedDocs) {
        const csvDoc = data.export_data.exportedDocs.find((doc: any) => doc.type === 1);
        
        if (csvDoc?.data) {
          const csvData = base64ToUtf8(csvDoc.data);
          const parsed = parseExportedTariffs(csvData);
          
          // Buscar la tarifa con el mismo código que la predeterminada
          const matchingTariff = parsed.find(
            (t: ExportedTariff) => t.cod_tarifa === defaultTariff.cod_tarifa_hioffice.toString()
          );
          
          if (matchingTariff) {
            // Helper para parsear valores decimales
            const parseDecimal = (value: string | null | undefined): number | null => {
              if (!value || value.trim() === '') return null;
              const parsed = parseFloat(value.replace(',', '.'));
              return isNaN(parsed) ? null : parsed;
            };
            
            // Actualizar automáticamente la tarifa predeterminada
            const updateRes = await apiFetch(
              'tariffs/create',
              {
                method: 'POST',
                body: JSON.stringify({
                  establishment_id: parseInt(selectedEstablishmentId, 10),
                  cod_tarifa_hioffice: parseInt(matchingTariff.cod_tarifa, 10),
                  tarifa_name: matchingTariff.tarifa_venta,
                  precio_extras: parseDecimal(matchingTariff.precio_extras),
                  precio_extras_2: parseDecimal(matchingTariff.precio_extras_2),
                  precio_hazlo_combo_regular: parseDecimal(matchingTariff.precio_hazlo_combo_regular),
                  precio_hazlo_combo_gigante: parseDecimal(matchingTariff.precio_hazlo_combo_gigante),
                  precio_refresco_regular: parseDecimal(matchingTariff.precio_refresco_regular),
                  kcal_refresco_regular: parseDecimal(matchingTariff.kcal_refresco_regular),
                  precio_refresco_large: parseDecimal(matchingTariff.precio_refresco_large),
                  kcal_refresco_large: parseDecimal(matchingTariff.kcal_refresco_large),
                  precio_refresco_bottle: parseDecimal(matchingTariff.precio_refresco_bottle),
                  kcal_refresco_bottle: parseDecimal(matchingTariff.kcal_refresco_bottle),
                  precio_chips: parseDecimal(matchingTariff.precio_chips),
                  kcal_chips: parseDecimal(matchingTariff.kcal_chips),
                  precio_cookie: parseDecimal(matchingTariff.precio_cookie),
                  kcal_cookie: parseDecimal(matchingTariff.kcal_cookie),
                  precio_brownie: parseDecimal(matchingTariff.precio_brownie),
                  kcal_brownie: parseDecimal(matchingTariff.kcal_brownie)
                })
              },
              token
            );
            
            if (isNetworkErrorResponse(updateRes)) {
              toast.error('Error de conexión al actualizar tarifa');
              return;
            }
            
            const updateJson = await parseJsonResponse(updateRes);
            
            if (updateJson.error) {
              const errorMessage = typeof updateJson.error === 'string' ? updateJson.error : updateJson.message || 'Error al actualizar tarifa';
              toast.error(errorMessage);
            } else {
              toast.success('Tarifa predeterminada actualizada exitosamente');
              // Recargar tarifas
              loadTariffs();
            }
          } else {
            toast.warning('No se encontró la tarifa predeterminada en los datos exportados');
            // Recargar tarifas de todas formas
            loadTariffs();
          }
        } else {
          toast.warning('No se encontraron datos CSV en la exportación');
          loadTariffs();
        }
      } else {
        toast.warning('No se encontraron datos de exportación');
        loadTariffs();
      }
    } catch (error: any) {
      console.error('Error al refrescar tarifa predeterminada:', error);
      toast.error('Error al refrescar tarifa predeterminada');
    } finally {
      setSyncing(false);
    }
  };

  // Función separada para guardar la tarifa (después de confirmación)
  const saveTariffConfirmed = async (tariff: ExportedTariff) => {
    if (!token || !selectedEstablishmentId) return;
    
    try {
      // Helper para parsear valores decimales
      const parseDecimal = (value: string | null | undefined): number | null => {
        if (!value || value.trim() === '') return null;
        const parsed = parseFloat(value.replace(',', '.'));
        return isNaN(parsed) ? null : parsed;
      };
      
      const res = await apiFetch(
        'tariffs/create',
        {
          method: 'POST',
          body: JSON.stringify({
            establishment_id: parseInt(selectedEstablishmentId, 10),
            cod_tarifa_hioffice: parseInt(tariff.cod_tarifa, 10),
            tarifa_name: tariff.tarifa_venta,
            precio_extras: parseDecimal(tariff.precio_extras),
            precio_extras_2: parseDecimal(tariff.precio_extras_2),
            precio_hazlo_combo_regular: parseDecimal(tariff.precio_hazlo_combo_regular),
            precio_hazlo_combo_gigante: parseDecimal(tariff.precio_hazlo_combo_gigante),
            precio_refresco_regular: parseDecimal(tariff.precio_refresco_regular),
            kcal_refresco_regular: parseDecimal(tariff.kcal_refresco_regular),
            precio_refresco_large: parseDecimal(tariff.precio_refresco_large),
            kcal_refresco_large: parseDecimal(tariff.kcal_refresco_large),
            precio_refresco_bottle: parseDecimal(tariff.precio_refresco_bottle),
            kcal_refresco_bottle: parseDecimal(tariff.kcal_refresco_bottle),
            precio_chips: parseDecimal(tariff.precio_chips),
            kcal_chips: parseDecimal(tariff.kcal_chips),
            precio_cookie: parseDecimal(tariff.precio_cookie),
            kcal_cookie: parseDecimal(tariff.kcal_cookie),
            precio_brownie: parseDecimal(tariff.precio_brownie),
            kcal_brownie: parseDecimal(tariff.kcal_brownie)
          })
        },
        token
      );
      
      if (isNetworkErrorResponse(res)) {
        toast.error('Error de conexión al guardar tarifa');
        return;
      }
      
      const json = await parseJsonResponse(res);
      
      if (json.error || !json.success) {
        let errorMessage = 'Error al guardar tarifa';
        if (json.error) {
          errorMessage = typeof json.error === 'string' ? json.error : String(json.error);
        } else if (json.message) {
          errorMessage = typeof json.message === 'string' ? json.message : String(json.message);
        }
        toast.error(errorMessage);
      } else {
        toast.success('Tarifa guardada exitosamente');
        loadTariffs();
        setSelectedTariff('');
        setExportedTariffs([]);
      }
    } catch (error: any) {
      console.error('Error al guardar tarifa:', error);
      toast.error('Error al guardar tarifa');
    }
  };

  const handleSaveTariff = async () => {
    if (!token || !selectedEstablishmentId || !selectedTariff) {
      toast.error('Debes seleccionar una tarifa');
      return;
    }
    
    const tariffIndex = parseInt(selectedTariff.split('-')[1], 10);
    const tariff = exportedTariffs[tariffIndex];
    
    if (!tariff) {
      toast.error('Tarifa no válida');
      return;
    }
    
    // Verificar si ya existe una tarifa guardada
    if (tariffs.length > 0) {
      // Mostrar advertencia de confirmación
      toast.warning('¿Cambiar tarifa?', {
        description: 'Si cambias la tarifa, deberás configurar nuevamente las pantallas. ¿Estás seguro?',
        duration: 10000, // 10 segundos para dar tiempo a leer
        action: {
          label: 'Sí, cambiar Tarifa',
          onClick: () => {
            // Proceder con el guardado
            saveTariffConfirmed(tariff);
          },
        },
        cancel: {
          label: 'Cancelar',
          onClick: () => {
            // No hacer nada, solo cerrar el toast
          },
        },
      });
      return;
    }
    
    // Si no hay tarifa existente, guardar directamente
    saveTariffConfirmed(tariff);
  };


  return (
    <DashboardLayout
      title="Gestión de Tarifas"
      subtitle="Administra las tarifas de los establecimientos"
      showBackButton
      backPath="/management"
    >
      <div className="space-y-6">
        {/* Selector de Establecimiento */}
        <Card>
          <CardContent className="p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center">
              <div className="flex-1">
                <Select
                  label="Seleccionar Establecimiento"
                  value={selectedEstablishmentId}
                  onChange={(e) => setSelectedEstablishmentId(e.target.value)}
                  options={[
                    { value: '', label: '-- Selecciona un establecimiento --' },
                    ...establishments.filter(e => e.is_active === 1).map(e => ({
                      value: e.id.toString(),
                      label: `${e.name_establishment} (Cód: ${e.code_establishment_hioffice})`
                    }))
                  ]}
                  disabled={loadingEstablishments}
                />
              </div>
              {selectedEstablishmentId && (
                <Button
                  variant="primary"
                  size="md"
                  onClick={handleSync}
                  isLoading={syncing}
                  disabled={syncing || loadingEstablishments}
                  className="whitespace-nowrap"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Sincronizar desde Hioffice
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Error Message */}
        {error && (
          <Alert variant="error">{error}</Alert>
        )}

        {!selectedEstablishmentId ? (
          <Card>
            <CardContent>
              <div className="p-12 text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Selecciona un Establecimiento</h3>
                <p className="text-gray-600">
                  Elige un establecimiento del menú desplegable para ver y gestionar sus tarifas.
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Selector de Tarifa Exportada */}
            {exportedTariffs.length > 0 && (
              <Card>
                <CardContent className="p-4 sm:p-6">
                  <div className="p-6">
                    <label className="block text-sm font-semibold text-gray-700 mb-4">
                      Agregar nueva tarifa desde Hioffice
                    </label>
                    <Select
                      label="Tarifas disponibles"
                      options={exportedTariffs.map((tariff, index) => ({
                        value: `${tariff.cod_tarifa}-${index}`,
                        label: `${tariff.tarifa_venta} (Cód: ${tariff.cod_tarifa}) - ${tariff.establecimiento}`
                      }))}
                      value={selectedTariff}
                      onChange={(e) => setSelectedTariff(e.target.value)}
                      helperText={`${exportedTariffs.length} tarifa(s) disponible(s) desde Hioffice`}
                    />
                    {selectedTariff && (
                      <div className="mt-4">
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={handleSaveTariff}
                        >
                          Guardar Tarifa
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Lista de Tarifas */}
            {loading ? (
              <Card>
                <CardContent>
                  <div className="p-12 text-center">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mb-4"></div>
                    <p className="text-gray-600">Cargando tarifas...</p>
                  </div>
                </CardContent>
              </Card>
            ) : tariffs.length === 0 ? (
              <Card>
                <CardContent>
                  <div className="p-12 text-center text-gray-500">
                    <svg className="w-12 h-12 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p className="font-medium mb-2">No hay tarifas sincronizadas</p>
                    <p className="text-sm">
                      Haz clic en "Sincronizar desde Hioffice" para obtener las tarifas de este establecimiento
                    </p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {tariffs.map((tariff) => (
                  <Card key={tariff.id} hover={tariff.is_default !== 1}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-gray-900 mb-1 truncate">
                            {tariff.tarifa_name}
                          </h4>
                          <p className="text-xs text-gray-500">
                            Código: {tariff.cod_tarifa_hioffice}
                          </p>
                        </div>
                        {tariff.is_default === 1 && (
                          <Badge variant="success" className="ml-2 flex-shrink-0">
                            Predeterminada
                          </Badge>
                        )}
                      </div>
                      
                      {tariff.last_sync && (
                        <>
                          <p className="text-xs text-gray-400 mb-2">
                            Sincronizado: {new Date(tariff.last_sync).toLocaleDateString('es-MX', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                          {/* Botón de actualizar para tarifa predeterminada */}
                          {tariff.is_default === 1 && (
                            <button
                              type="button"
                              onClick={() => handleRefreshDefaultTariff(tariff)}
                              disabled={syncing}
                              className="w-full py-2 px-3 rounded-lg bg-white hover:bg-gray-50 border border-gray-200 text-gray-600 hover:text-gray-900 hover:border-gray-300 transition-all duration-200 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm font-medium"
                            >
                              {syncing ? (
                                <>
                                  <svg 
                                    className="w-4 h-4 animate-spin text-red-600" 
                                    fill="none" 
                                    viewBox="0 0 24 24"
                                  >
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                  <span>Actualizando...</span>
                                </>
                              ) : (
                                <>
                                  <svg 
                                    className="w-4 h-4" 
                                    fill="none" 
                                    stroke="currentColor" 
                                    viewBox="0 0 24 24"
                                  >
                                    <path 
                                      strokeLinecap="round" 
                                      strokeLinejoin="round" 
                                      strokeWidth={2.5} 
                                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" 
                                    />
                                  </svg>
                                  <span>Actualizar</span>
                                </>
                              )}
                            </button>
                          )}
                        </>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

